import type { PillTone } from '../../components/ui';
import type { AdaptedReport } from '../../components/titleAnalysis/reportAdapter';

/**
 * Pure display rules for the Title analysis report (no React), so the mapping
 * from report values to labels and tones can be tested on its own.
 *
 * Tones follow the status table in .kiro/steering/ui-design-system.md.
 *
 * Requirements: 11.6, 11.7, 11.8.
 */

export type Grade = AdaptedReport['grade'];
export type LienStatus = AdaptedReport['lienPosition']['status'];
export type ReportException = AdaptedReport['exceptions'][number];
export type ReportEncumbrance = AdaptedReport['encumbrances'][number];
export type ReportVestingLink = AdaptedReport['vestingChain'][number];

export const DISCLAIMER_TEXT =
  'Exception report for review by a title examiner, attorney, or diligence analyst. Not a title opinion, commitment, or legal advice.';

// ---------------------------------------------------------------------------
// Grade
// ---------------------------------------------------------------------------

export interface GradeStyle {
  /** Tailwind classes for the grade card surface. */
  card: string;
  /** Tailwind classes for the large letter tile. */
  tile: string;
  description: string;
}

const GRADE_GOOD: Pick<GradeStyle, 'card' | 'tile'> = {
  card: 'border-[#bbf7d0] bg-[#f0fdf4]',
  tile: 'bg-[#dcfce7] text-[#166534]',
};
// Grade C/D card: #fff7ed bg, #fdba74 border, #c2410c text.
const GRADE_ATTENTION: Pick<GradeStyle, 'card' | 'tile'> = {
  card: 'border-[#fdba74] bg-[#fff7ed]',
  tile: 'bg-[#ffedd5] text-[#c2410c]',
};

export function gradeStyle(grade: Grade): GradeStyle {
  switch (grade) {
    case 'A':
      return { ...GRADE_GOOD, description: 'Minimal exceptions' };
    case 'B':
      return { ...GRADE_GOOD, description: 'Moderate exceptions' };
    case 'C':
      return { ...GRADE_ATTENTION, description: 'Significant exceptions' };
    case 'D':
      return { ...GRADE_ATTENTION, description: 'Major exceptions' };
    default:
      return {
        card: 'border-gray-200 bg-white',
        tile: 'bg-[#f3f4f6] text-[#4b5563]',
        description: 'Grade unavailable',
      };
  }
}

// ---------------------------------------------------------------------------
// Status cards
// ---------------------------------------------------------------------------

export function lienStatusMeta(status: LienStatus): { label: string; tone: PillTone } {
  switch (status) {
    case 'confirmed':
      return { label: 'Confirmed', tone: 'success' };
    case 'mismatch':
      return { label: 'Mismatch', tone: 'danger' };
    default:
      return { label: 'Undetermined', tone: 'neutral' };
  }
}

/** 1 -> "1st lien", 2 -> "2nd lien", 11 -> "11th lien". Null -> "Undetermined". */
export function lienPositionLabel(position: number | null | undefined): string {
  if (typeof position !== 'number' || !Number.isFinite(position)) return 'Undetermined';
  const mod100 = position % 100;
  let suffix = 'th';
  if (mod100 < 11 || mod100 > 13) {
    const mod10 = position % 10;
    if (mod10 === 1) suffix = 'st';
    else if (mod10 === 2) suffix = 'nd';
    else if (mod10 === 3) suffix = 'rd';
  }
  return `${position}${suffix} lien`;
}

export function formatMoney(amount: number | null | undefined): string {
  return typeof amount === 'number' ? `$${amount.toLocaleString()}` : '\u2014';
}

// ---------------------------------------------------------------------------
// Exceptions, links, encumbrances
// ---------------------------------------------------------------------------

export function severityTone(severity: string): PillTone {
  switch (severity) {
    case 'material':
      return 'danger';
    case 'curable':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function confidenceTone(confidence: string): PillTone {
  switch (confidence) {
    case 'high':
      return 'success';
    case 'medium':
      return 'warning';
    case 'low':
      return 'danger';
    default:
      return 'neutral';
  }
}

export type SeverityFilter = 'all' | 'material' | 'curable' | 'informational';

export const SEVERITY_FILTERS: ReadonlyArray<{ id: SeverityFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'material', label: 'Material' },
  { id: 'curable', label: 'Curable' },
  { id: 'informational', label: 'Informational' },
];

/** Exceptions matching the filter, in their original order. 'all' keeps everything. */
export function filterExceptionsBySeverity(
  exceptions: ReportException[],
  filter: SeverityFilter
): ReportException[] {
  if (filter === 'all') return [...exceptions];
  return exceptions.filter((e) => e.severity === filter);
}

const SEVERITY_ORDER: Record<string, number> = { material: 0, curable: 1, informational: 2 };

/**
 * Material first, then curable, then informational. Order within a severity is
 * preserved (Array.prototype.sort is stable).
 */
export function sortExceptionsBySeverity(exceptions: ReportException[]): ReportException[] {
  const rank = (e: ReportException) => SEVERITY_ORDER[e.severity] ?? SEVERITY_ORDER.informational;
  return [...exceptions].sort((a, b) => rank(a) - rank(b));
}

/**
 * Count per filter. Material, Curable and Informational are disjoint and sum to
 * All, because the adapter only emits those three severities.
 */
export function severityCounts(exceptions: ReportException[]): Record<SeverityFilter, number> {
  return {
    all: exceptions.length,
    material: filterExceptionsBySeverity(exceptions, 'material').length,
    curable: filterExceptionsBySeverity(exceptions, 'curable').length,
    informational: filterExceptionsBySeverity(exceptions, 'informational').length,
  };
}

export function categoryLabel(category: string): string {
  switch (category) {
    case 'vesting':
      return 'Vesting';
    case 'encumbrance':
      return 'Encumbrance';
    case 'completeness':
      return 'Completeness';
    case 'commitment':
      return 'Commitment';
    default:
      return category;
  }
}

export function linkMeta(link: ReportVestingLink['linkToPrior']): {
  label: string;
  tone: PillTone;
} {
  switch (link) {
    case 'valid':
      return { label: 'Valid link', tone: 'success' };
    case 'first_in_search':
      return { label: 'First in search', tone: 'neutral' };
    case 'name_variance':
      return { label: 'Name variance', tone: 'warning' };
    case 'broken':
      return { label: 'Broken link', tone: 'danger' };
    default:
      return { label: String(link), tone: 'neutral' };
  }
}

export function encumbranceTypeLabel(type: string): string {
  switch (type) {
    case 'mortgage':
      return 'Mortgage';
    case 'judgment':
      return 'Judgment';
    case 'tax_lien':
      return 'Tax lien';
    case 'hoa_assessment':
      return 'HOA assessment';
    case 'mechanic_lien':
      return "Mechanic's lien";
    case 'other':
      return 'Other';
    default:
      return type;
  }
}

export function encumbranceStatusMeta(status: ReportEncumbrance['status']): {
  label: string;
  tone: PillTone;
} {
  switch (status) {
    case 'open':
      return { label: 'Open', tone: 'danger' };
    case 'released':
      return { label: 'Released', tone: 'success' };
    case 'expired':
      return { label: 'Expired', tone: 'success' };
    case 'presumed_expired':
      return { label: 'Presumed expired', tone: 'warning' };
    case 'extinguished':
      return { label: 'Extinguished', tone: 'success' };
    default:
      return { label: 'Undetermined', tone: 'neutral' };
  }
}

/** Highest priority first (lower rank), then by type for a stable order. */
export function sortEncumbrances(items: ReportEncumbrance[]): ReportEncumbrance[] {
  return [...items].sort(
    (a, b) => a.derivedPriorityRank - b.derivedPriorityRank || a.type.localeCompare(b.type)
  );
}

export function sortVestingChain(items: ReportVestingLink[]): ReportVestingLink[] {
  return [...items].sort((a, b) => a.sequence - b.sequence);
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

export type ReportTabId = 'exceptions' | 'vesting' | 'encumbrances' | 'parties' | 'commitment';

export const REPORT_TAB_LABELS: ReadonlyArray<{ id: ReportTabId; label: string }> = [
  { id: 'exceptions', label: 'Exceptions' },
  { id: 'vesting', label: 'Vesting chain' },
  { id: 'encumbrances', label: 'Encumbrances' },
  { id: 'parties', label: 'Parties' },
  { id: 'commitment', label: 'Commitment' },
];

/** Count shown beside a tab label; null for tabs without a list (Commitment). */
export function tabCount(tab: ReportTabId, report: AdaptedReport): number | null {
  switch (tab) {
    case 'exceptions':
      return report.exceptions.length;
    case 'vesting':
      return report.vestingChain.length;
    case 'encumbrances':
      return report.encumbrances.length;
    case 'parties':
      return report.entities.length;
    default:
      return null;
  }
}
